import React, { useState, useRef, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import FileUpload from "@/components/FileUpload.jsx";
import { PrimaryButton, SecondaryButton } from "@/components/Buttons.jsx";
import { addToast, fetchOnboardSteps } from "@/store/app/action.js";
import { uploadBusinessDocumentToS3 } from "@/api/upload.js";
import { createBusinessDocument, getBusinessDocuments, getBusinessDocumentUrl, getStore } from "@/api/settings.js";
import { updateSellerInfo } from "@/api/index.js";
import { FaFilePdf, FaFileWord, FaFileLines, FaArrowUpRightFromSquare } from "react-icons/fa6";
import InlineLoader from "@/components/InlineLoader.jsx";
import { formatPhoneNumber, parseAddress } from "@/utils/Utils.js";
import { Autocomplete } from "@react-google-maps/api";
import { useGoogleMaps } from "@/utils/googleMaps.jsx";

const DOCUMENT_TYPES = [
  "Tobacco License",
  "Alcohol License",
  "Other document"
];

const IF_APPLICABLE_DOCUMENT_TYPES = [
  "Tobacco License",
  "Alcohol License",
];

function BusinessPanel() {
  const dispatch = useDispatch();
  const { selectedLocation } = useSelector((state) => state.app);
  const [uploadStep, setUploadStep] = useState(1); // 1: select file, 2: configure, 3: uploading
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [documentType, setDocumentType] = useState("");
  const [otherDocumentType, setOtherDocumentType] = useState("");
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingUrl, setLoadingUrl] = useState(null);
  const processingRef = useRef(false);
  
  // Business information state
  const [businessInfo, setBusinessInfo] = useState({
    legalBusinessName: "",
    businessName: "",
    ein: "",
    businessAddress: "",
    businessPhoneNumber: "",
  });
  const [savingBusinessInfo, setSavingBusinessInfo] = useState(false);
  const [loadingBusinessInfo, setLoadingBusinessInfo] = useState(false);
  const [storeData, setStoreData] = useState(null);
  const [parsedAddress, setParsedAddress] = useState(null);
  
  // Google Maps autocomplete
  const addressAutocompleteRef = useRef(null);
  const { isLoaded } = useGoogleMaps();

  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getBusinessDocuments();
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Error loading documents:', error);
      dispatch(addToast({
        type: "error",
        message: "Failed to load documents",
        timer: 5
      }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  // Load documents on mount
  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // Load business info from store API
  const loadBusinessInfo = useCallback(async () => {
    if (!selectedLocation) return;
    
    try {
      setLoadingBusinessInfo(true);
      const res = await getStore();
      const store = res.store;
      
      if (store) {
        // Store the full store object for saving
        setStoreData(store);
        
        // Initialize parsed address from store data to preserve components
        if (store.address || store.city || store.state || store.zip) {
          setParsedAddress({
            address: store.address || "",
            city: store.city || "",
            state: store.state || "",
            zip: store.zip || "",
            location: store.location || null,
          });
        } else {
          setParsedAddress(null);
        }
        
        const phoneNumber = store.phone || "";
        
        // Format address with city, state, and zip if available
        let formattedAddress = store.business_address || store.address || "";
        if (formattedAddress && store.address_line2) {
          formattedAddress = `${formattedAddress} ${store.address_line2}`;
        }
        
        // Add city, state, and zip if available
        if (store.city || store.state || store.zip) {
          const cityStateZip = [
            store.city,
            store.state ? `${store.state} ${store.zip || ""}`.trim() : store.zip
          ].filter(Boolean).join(", ");
          
          if (cityStateZip) {
            formattedAddress = formattedAddress 
              ? `${formattedAddress}, ${cityStateZip}`
              : cityStateZip;
          }
        }
        
        setBusinessInfo({
          legalBusinessName: store.legal_name || "",
          businessName: store.name || "",
          ein: store.ein ? formatEIN(store.ein) : "",
          businessAddress: formattedAddress,
          businessPhoneNumber: phoneNumber ? formatPhoneNumber(phoneNumber) || phoneNumber : "",
        });
      }
    } catch (error) {
      console.error('Error loading business information:', error);
      dispatch(addToast({
        type: "error",
        message: "Failed to load business information",
        timer: 5
      }));
    } finally {
      setLoadingBusinessInfo(false);
    }
  }, [selectedLocation, dispatch]);

  // Load business info on mount and when selectedLocation changes
  useEffect(() => {
    loadBusinessInfo();
  }, [loadBusinessInfo]);

  const handleBusinessInfoChange = (field, value) => {
    setBusinessInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatEIN = (value) => {
    // Remove all non-numeric characters
    const numbers = value.replace(/\D/g, '');
    
    // Limit to 9 digits
    const limited = numbers.slice(0, 9);
    
    // Format as XX-XXXXXXX
    if (limited.length <= 2) {
      return limited;
    } else {
      return `${limited.slice(0, 2)}-${limited.slice(2)}`;
    }
  };

  const handleEINChange = (e) => {
    const formatted = formatEIN(e.target.value);
    handleBusinessInfoChange("ein", formatted);
  };

  const handleAddressPlaceChanged = () => {
    if (addressAutocompleteRef.current) {
      const place = addressAutocompleteRef.current.getPlace();
      if (place) {
        const parsed = parseAddress(place);
        if (parsed) {
          // Store parsed address components
          setParsedAddress(parsed);
          
          // Format address as a string for display
          const addressParts = [
            parsed.address,
            parsed.city,
            parsed.state,
            parsed.zip
          ].filter(Boolean);
          const formattedAddress = addressParts.join(", ");
          
          setBusinessInfo(prev => ({
            ...prev,
            businessAddress: formattedAddress
          }));
        }
      }
    }
  };

  const handleSaveBusinessInfo = async () => {
    if (!storeData) {
      dispatch(addToast({
        type: "error",
        message: "Store data not loaded. Please refresh the page.",
        timer: 5
      }));
      return;
    }
    
    setSavingBusinessInfo(true);
    try {
      // Update the store object with new business information
      // Remove dash from EIN before saving (API expects just numbers)
      const einNumbers = businessInfo.ein.replace(/\D/g, '');
      
      const updatedStore = {
        ...storeData,
        legal_name: businessInfo.legalBusinessName,
        name: businessInfo.businessName,
        ein: einNumbers,
        phone: businessInfo.businessPhoneNumber,
      };
      
      // Update address components if we have parsed address from Google autocomplete
      if (parsedAddress) {
        updatedStore.address = parsedAddress.address || storeData.address || "";
        updatedStore.address_line2 = storeData.address_line2 || ""; // Preserve existing address_line2
        updatedStore.city = parsedAddress.city || storeData.city || "";
        updatedStore.state = parsedAddress.state || storeData.state || "";
        updatedStore.zip = parsedAddress.zip || storeData.zip || "";
        if (parsedAddress.location) {
          updatedStore.location = parsedAddress.location;
        }
      } else {
        // If no parsed address, keep existing address components
        // The formatted address string is just for display
        updatedStore.address = storeData.address || "";
        updatedStore.address_line2 = storeData.address_line2 || "";
        updatedStore.city = storeData.city || "";
        updatedStore.state = storeData.state || "";
        updatedStore.zip = storeData.zip || "";
      }
      
      await updateSellerInfo({basic: updatedStore});
      
      // Reload business info to get updated data
      await loadBusinessInfo();
      
      dispatch(addToast({
        type: "success",
        message: "Business information saved successfully",
        timer: 4
      }));
    } catch (error) {
      console.error('Error saving business information:', error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to save business information. Please try again.";
      dispatch(addToast({
        type: "error",
        message: errorMessage,
        timer: 5
      }));
    } finally {
      if(selectedLocation?.prospect) {
        dispatch(fetchOnboardSteps());
      }
      setSavingBusinessInfo(false);
    }
  };

  // File icon helper function
  const getFileIcon = (filename, size = "w-8 h-8") => {
    const extension = filename?.split('.').pop().toLowerCase() || "";
    switch (extension) {
      case 'pdf':
        return <FaFilePdf className={`${size} text-red-500`} />;
      case 'doc':
      case 'docx':
        return <FaFileWord className={`${size} text-blue-500`} />;
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
      case 'webp':
        return <FaFileLines className={`${size} text-green-500`} />;
      default:
        return <FaFileLines className={`${size} text-slate-500`} />;
    }
  };

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    // Prevent multiple simultaneous processing
    if (processingRef.current) {
      return;
    }
    
    // Validate file types
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/gif',
      'image/webp'
    ];
    const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));
    
    if (invalidFiles.length > 0) {
      dispatch(addToast({
        type: "error",
        message: `Invalid file types. Only PDF, DOC, DOCX, PNG, JPEG, GIF, and WEBP files are allowed.`,
        timer: 5
      }));
      return;
    }
    
    // Take only the first file
    const file = files[0];
    setSelectedFile(file);
    setDocumentType(""); // Reset document type selection
    
    // Upload file to S3 first
    processingRef.current = true;
    setUploading(true);
    setUploadStep(3);
    
    try {
      const uploadedUrl = await uploadBusinessDocumentToS3(file);
      setFileUrl(uploadedUrl);
      // Move to configuration step
      setUploadStep(2);
    } catch (error) {
      console.error('Error uploading file to S3:', error);
      dispatch(addToast({
        type: "error",
        message: "Error uploading file. Please try again.",
        timer: 5
      }));
      setUploadStep(1);
    } finally {
      setUploading(false);
      processingRef.current = false;
    }
  };

  const handleConfirmUpload = async () => {
    if (!selectedFile || !fileUrl) return;
    
    if (!documentType) {
      dispatch(addToast({
        type: "error",
        message: "Please select a document type",
        timer: 5
      }));
      return;
    }

    if (documentType === "Other document" && !otherDocumentType.trim()) {
      dispatch(addToast({
        type: "error",
        message: "Please specify the document type",
        timer: 5
      }));
      return;
    }
    
    setUploading(true);
    try {      
      const documentData = {
        filename: selectedFile.name,
        key: fileUrl,
        document_type: documentType === "Other document" ? otherDocumentType.trim() : documentType
      };
      
      await createBusinessDocument(documentData);
      
      dispatch(addToast({
        type: "success",
        message: "Document uploaded successfully",
        timer: 4
      }));
      
      // Reload documents
      await loadDocuments();
      
      // Reset form
      setUploadStep(1);
      setSelectedFile(null);
      setFileUrl(null);
      setDocumentType("");
      setOtherDocumentType("");
      
    } catch (error) {
      console.error('Error saving document:', error);
      const errorMessage = error.response?.data?.message || error.message || "Error saving document. Please try again.";
      dispatch(addToast({
        type: "error",
        message: errorMessage,
        timer: 5
      }));
    } finally {
      if(selectedLocation?.prospect) {
        dispatch(fetchOnboardSteps());
      }
      setUploading(false);
    }
  };

  const handleCancelUpload = () => {
    setUploadStep(1);
    setSelectedFile(null);
    setFileUrl(null);
    setDocumentType("");
    setOtherDocumentType("");
  };

  const handleViewDocument = async (document) => {
    try {
      setLoadingUrl(document.id);
      const response = await getBusinessDocumentUrl(document.id);
      const authenticatedUrl = response.url || response.data?.url;
      
      if (authenticatedUrl) {
        window.open(authenticatedUrl, '_blank', 'noopener,noreferrer');
      } else {
        dispatch(addToast({
          type: "error",
          message: "Failed to get document URL",
          timer: 5
        }));
      }
    } catch (error) {
      console.error('Error getting document URL:', error);
      dispatch(addToast({
        type: "error",
        message: "Failed to open document. Please try again.",
        timer: 5
      }));
    } finally {
      setLoadingUrl(null);
    }
  };

  return (
    <div className="grow flex flex-col md:flex-row">
      {/* Panel */}
      <div className="grow">
        <div className="p-6 space-y-6">
          {/* Business Information Section */}
          <section>
            <h2 className="text-xl leading-snug text-slate-800 dark:text-slate-100 font-semibold mb-1">
              Business Information
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
              Manage your business details and contact information
            </p>

            {loadingBusinessInfo ? (
              <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6">
                <InlineLoader message="Loading business information..." />
              </div>
            ) : (
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Legal Business Name
                  </label>
                  <input
                    type="text"
                    className="form-input w-full"
                    placeholder="Enter legal business name"
                    value={businessInfo.legalBusinessName}
                    onChange={(e) => handleBusinessInfoChange("legalBusinessName", e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Business Name (Doing Business As)
                  </label>
                  <input
                    type="text"
                    className="form-input w-full"
                    placeholder="Enter DBA name"
                    value={businessInfo.businessName}
                    onChange={(e) => handleBusinessInfoChange("businessName", e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Employer Identification Number (EIN)
                  </label>
                  <input
                    type="text"
                    className="form-input w-full"
                    placeholder="XX-XXXXXXX"
                    value={businessInfo.ein}
                    onChange={handleEINChange}
                    maxLength={10}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Business Phone Number
                  </label>
                  <input
                    type="text"
                    className="form-input w-full"
                    placeholder="(XXX) XXX-XXXX"
                    value={businessInfo.businessPhoneNumber}
                    onChange={(e) => {
                      const formatted = formatPhoneNumber(e.target.value) || e.target.value;
                      handleBusinessInfoChange("businessPhoneNumber", formatted);
                    }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Business Address
                </label>
                {isLoaded ? (
                  <Autocomplete
                    onLoad={(autocomplete) => {
                      addressAutocompleteRef.current = autocomplete;
                    }}
                    onPlaceChanged={handleAddressPlaceChanged}
                    options={{
                      types: ["address"],
                      componentRestrictions: { country: "us" },
                    }}
                  >
                    <input
                      type="text"
                      className="form-input w-full"
                      placeholder="Enter business address"
                      value={businessInfo.businessAddress}
                      onChange={(e) => handleBusinessInfoChange("businessAddress", e.target.value)}
                    />
                  </Autocomplete>
                ) : (
                  <input
                    type="text"
                    className="form-input w-full"
                    placeholder="Loading address autocomplete..."
                    disabled
                  />
                )}
              </div>

              <div className="flex justify-end pt-4 border-t border-slate-200 dark:border-slate-700">
                <PrimaryButton loading={savingBusinessInfo} onClick={handleSaveBusinessInfo} disabled={savingBusinessInfo}>
                  Save Business Information
                </PrimaryButton>
              </div>
            </div>
            )}
          </section>

          {/* Business Document Upload Section */}
          <section id="documents">
            <h2 className="text-xl leading-snug text-slate-800 dark:text-slate-100 font-semibold mb-1">
              Business Documents
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
              Upload your business licenses and documents
            </p>

            {/* Step 1: File Selection */}
            {uploadStep === 1 && (
              <div className="space-y-4">
                {/* Document Types List */}
                <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 mb-4">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                    Required Documents
                  </h3>
                  <ul className="space-y-2">
                    {DOCUMENT_TYPES.filter(type => type !== "Other document").map((type) => (
                      <li key={type} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400">
                        <svg
                          className="w-5 h-5 shrink-0 mt-0.5 text-slate-400 dark:text-slate-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span>{type} {IF_APPLICABLE_DOCUMENT_TYPES.includes(type) ? <span className="text-slate-500 dark:text-slate-400 text-xs">(If applicable)</span> : ""} </span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                    You can also upload other business-related documents by selecting "Other document" during upload.
                  </p>
                </div>

                <FileUpload
                  accept={[".pdf", ".doc", ".docx", ".png", ".jpg", ".jpeg", ".gif", ".webp", "image/*"]}
                  multiple={false}
                  onChange={handleFileUpload}
                  inputContent="Drag & drop a document or image here or click to browse (PDF, DOC, DOCX, PNG, JPEG, GIF, WEBP)"
                />
              </div>
            )}

            {/* Step 2: Configuration */}
            {uploadStep === 2 && selectedFile && (
              <div className="space-y-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className="flex items-center gap-3">
                  {getFileIcon(selectedFile?.name || "")}
                  <div className="flex-1">
                    <h5 className="font-medium text-slate-800 dark:text-slate-100">
                      {selectedFile.name}
                    </h5>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {/* Document Type Selection */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Document Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      className="form-select w-full"
                      value={documentType}
                      onChange={(e) => setDocumentType(e.target.value)}
                    >
                      <option value="">Select document type...</option>
                      {DOCUMENT_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Other Document Type Input */}
                  {documentType === "Other document" && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Specify Document Type <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-input w-full"
                        placeholder="Enter document type..."
                        value={otherDocumentType}
                        onChange={(e) => setOtherDocumentType(e.target.value)}
                      />
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2 justify-end">
                    <SecondaryButton onClick={handleCancelUpload} disabled={uploading}>
                      Cancel
                    </SecondaryButton>
                    <PrimaryButton onClick={handleConfirmUpload} loading={uploading} disabled={uploading}>
                      Save Document
                    </PrimaryButton>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Uploading */}
            {uploadStep === 3 && (
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <InlineLoader message="Uploading document..." />
              </div>
            )}

            {/* Uploaded Documents List */}
            {uploadStep === 1 && (
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
                  Uploaded Documents
                </h3>
                {loading ? (
                  <div className="p-8 text-center">
                    <InlineLoader message="Loading documents..." />
                  </div>
                ) : documents.length === 0 ? (
                  <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                    <p>No documents uploaded yet.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {documents.map((doc) => (
                      <div
                        key={doc.id}
                        className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4 flex items-center gap-4"
                      >
                        <div className="w-12 h-12 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center flex-shrink-0">
      
                            {getFileIcon(doc.original_name || doc.url, "w-6 h-6")}

                        </div>
                        <div className="flex-1 min-w-0">
                          <h5 className="font-medium text-slate-800 dark:text-slate-100 truncate">
                            {doc.original_name}
                          </h5>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {doc.document_type} • {new Date(doc.created_at || doc.created).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleViewDocument(doc)}
                            disabled={loadingUrl === doc.id}
                            className="p-2 text-slate-400 hover:text-indigo-500 dark:hover:text-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="View document"
                          >
                            {loadingUrl === doc.id ? (
                              <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <FaArrowUpRightFromSquare className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

export default BusinessPanel;

